/**
 * Generic "a tour wants this UI to behave differently" signal.
 *
 * Tours sometimes need a page component to cooperate — e.g. keep a Radix
 * Popover open while the tour teaches what's inside it, or pause filter
 * application while the user explores rows. The naive approach is to have
 * the page read `document.body.dataset.<specificTourName>`, which couples
 * the page to a specific tour's implementation.
 *
 * This module flips that contract:
 *   - Tours call `acquireTourLock('<capability-key>')` in setup and
 *     `releaseTourLock('<capability-key>')` in teardown.
 *   - Pages call `isTourLocked('<capability-key>')` from event handlers.
 *
 * The page knows "any tour can request to keep me open" — it does not know
 * which tour, or why. The tour knows "I want this lock" — it does not know
 * how the page implements it. The contract is named after the CAPABILITY
 * (`source-filter`), not the tour (`source-learn`).
 *
 * Implementation: a body-dataset flag named `data-tour-lock-<key>`. The CSS
 * counterpart lives in `lib/tours/styles.css` — selectors of the form
 * `body[data-tour-lock-<key>='true'] …`. The body-attribute is also why
 * `isTourLocked` is non-reactive: pages read it from event handlers, not
 * from render. If you ever need a reactive subscription, wrap with
 * useSyncExternalStore + a module-level Set of listeners.
 */

function flagAttr(key: string): string {
  // 'source-filter' → 'data-tour-lock-source-filter'
  return `data-tour-lock-${key}`
}

export function acquireTourLock(key: string): void {
  document.body.setAttribute(flagAttr(key), 'true')
}

export function releaseTourLock(key: string): void {
  document.body.removeAttribute(flagAttr(key))
}

export function isTourLocked(key: string): boolean {
  return document.body.getAttribute(flagAttr(key)) === 'true'
}
